function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Context that holds methods to track the state of all calls to usePersistentState below this provider.
 */
export var PersistentStateContext = /*#__PURE__*/createContext(null);
PersistentStateContext.displayName = 'PersistentStateContext';
function makeKey(dhId, type) {
  return "".concat(dhId, "::").concat(type);
}
/**
 * Tracks all calls to the usePersistentState hook below this provider.
 * Keeps track of the state based on dhId and state type, and calls onChange when the state changes.
 * Limit to one state per (dhId, type) pair.
 */
export function PersistentStateProvider(props) {
  var initialState = props.initialState,
    onChange = props.onChange,
    children = props.children;
  var isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);
  var needsMigrationToMap = useRef(false);
  var _useState = useState(() => {
      if (Array.isArray(initialState) && initialState.flat().length === 2 * initialState.length) {
        return new Map(initialState);
      }
      needsMigrationToMap.current = true;
      // Used to migrate from array to map
      return new Map();
    }),
    _useState2 = _slicedToArray(_useState, 1),
    stateMap = _useState2[0];

  // Track which hookId currently owns each (dhId, type) pair
  var hookOwnership = useRef(new Map());
  var addState = useCallback((hookId, dhId, state) => {
    var type = state.type;
    var key = makeKey(dhId, type);
    var currentOwner = hookOwnership.current.get(key);
    if (currentOwner != null && currentOwner !== hookId) {
      throw new Error("Detected multiple persistent states of type ".concat(type, " for dhId ").concat(dhId, ". Only one state per (dhId, type) pair is allowed."));
    }
    hookOwnership.current.set(key, hookId);
    if (isMounted.current && stateMap.get(key) !== state) {
      stateMap.set(key, state);
      onChange([...stateMap.entries()]);
    }
  }, [isMounted, onChange, stateMap]);
  var initialStateIterator = useRef(initialState[Symbol.iterator]());
  var getState = useCallback((id, type) => {
    var key = makeKey(id, type);
    var state = stateMap.get(key);
    if (state == null && needsMigrationToMap.current) {
      var _initialStateIterator = initialStateIterator.current.next(),
        value = _initialStateIterator.value;
      var stateVal = value;
      stateMap.set(key, stateVal);
      return stateVal;
    }
    return state;
  }, [stateMap]);
  var removeState = useCallback((hookId, dhId, type) => {
    var key = makeKey(dhId, type);
    var currentOwner = hookOwnership.current.get(key);

    // Only remove if this hookId is the current owner
    if (currentOwner === hookId) {
      stateMap.delete(key);
      hookOwnership.current.delete(key);
      if (isMounted.current) {
        onChange([...stateMap.entries()]);
      }
    }
  }, [isMounted, onChange, stateMap]);
  var deregisterHook = useCallback((hookId, dhId, type) => {
    var key = makeKey(dhId, type);
    var currentOwner = hookOwnership.current.get(key);

    // Only remove if this hookId is the current owner
    if (currentOwner === hookId) {
      hookOwnership.current.delete(key);
    }
  }, []);
  var contextValue = useMemo(() => ({
    addState,
    removeState,
    deregisterHook,
    getState
  }), [addState, getState, removeState, deregisterHook]);
  return /*#__PURE__*/_jsx(PersistentStateContext.Provider, {
    value: contextValue,
    children: children
  });
}
//# sourceMappingURL=PersistentStateContext.js.map